import type { ApplicationStatusResponse } from "@credit-acceptance/step6-bff-client";

import { descriptionsForCodes } from "./dealState/dealStatusCatalog";
import type { WorkbenchRoleCapabilities } from "./types";

const READY_FOR_REVIEW_STATUSES = descriptionsForCodes("RR");

export interface WorkbenchPanelPermissions {
  actions: {
    canRunActions: boolean;
    canEditAnalystNotes: boolean;
    analystNotesOnly: boolean;
  };
  stipulations: {
    canEdit: boolean;
  };
  documents: {
    canUploadProofs: boolean;
    canManageDocuments: boolean;
  };
}

/**
 * Matrix sections 5.7-5.9: these panels have no program-role section markers.
 * Reads remain available; writes combine these gates with the BFF's action
 * list, stipulation flags, and document state. Department/proxy/audit mappings
 * remain unresolved and are deliberately not inferred here. Unlike applicant
 * forms, these workflows have no blanket CP/AH status restriction.
 * @param {WorkbenchRoleCapabilities} role - The resolved role.
 * @param {ApplicationStatusResponse | undefined} status - Current status/phase.
 * @param {boolean} isVlaHeldByMe - Whether this user holds the deal lock.
 * @param {boolean} ready - Whether role and status reads are trustworthy.
 * @returns {WorkbenchPanelPermissions} Panel-level ceilings on backend-granted access.
 */
export function resolvePanelPermissions(
  role: WorkbenchRoleCapabilities,
  status: ApplicationStatusResponse | undefined,
  isVlaHeldByMe: boolean,
  ready: boolean
): WorkbenchPanelPermissions {
  const roleName = role.roleName.trim().toUpperCase();
  const canWrite = ready && !!roleName && isVlaHeldByMe;
  const canReviewDocuments =
    roleName.includes("DSC FUNDING") ||
    roleName.includes("DEALER SUPPORT") ||
    roleName === "ITSUPPORT" ||
    roleName === "ITSECURITY";

  return {
    actions: {
      canRunActions: canWrite && role.isCacUser,
      canEditAnalystNotes: canWrite && role.isCacUser,
      analystNotesOnly: READY_FOR_REVIEW_STATUSES.has(status?.statusDescription ?? ""),
    },
    stipulations: { canEdit: canWrite && role.isCacUser },
    documents: {
      canUploadProofs: canWrite,
      canManageDocuments: canWrite && canReviewDocuments,
    },
  };
}
